<template>
    <Dialog v-model:visible="visible" :header="$t('profile.title')" class="mx-4 w-full md:w-2/5" modal @hide="reset">
        <AppForm class="flex flex-col gap-4" @submit="save">
            <AppInputText v-model="form.name" :label="$t('profile.name')" name="name" rules="required" />
            <AppInputText v-model="form.email" :label="$t('profile.email')" name="email" rules="email" type="email" />
            <AppDropdown
                v-model="form.language"
                :label="$t('profile.language')"
                name="language"
                :options="languageOptions"
            />

            <!-- Optional password change -->
            <div class="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                <div class="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">{{ $t('profile.change_password') }}</div>
                <div class="text-[11px] text-slate-400 mb-3">{{ $t('profile.change_password_hint') }}</div>
                <div class="flex flex-col gap-3">
                    <AppInputText v-model="form.current_password" :label="$t('profile.current_password')" name="current_password" type="password" />
                    <AppInputText v-model="form.new_password" :label="$t('profile.new_password')" name="new_password" type="password" />
                    <AppInputText v-model="confirmPassword" :label="$t('profile.confirm_password')" name="confirm_password" type="password" />
                </div>
            </div>

            <!-- Passkeys (WebAuthn). Hidden entirely when the server has no
                 relying party configured or the browser cannot do WebAuthn. -->
            <div v-if="passkeyAvailable" class="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                <div class="flex items-start justify-between gap-3 mb-1">
                    <div>
                        <div class="text-xs font-semibold text-slate-500 dark:text-slate-400">{{ $t('passkeys.title') }}</div>
                        <div class="text-[11px] text-slate-400">{{ $t('passkeys.hint') }}</div>
                    </div>
                    <Button
                        icon="pi pi-plus"
                        :label="$t('passkeys.add')"
                        :loading="passkeyBusy"
                        severity="secondary"
                        size="small"
                        type="button"
                        @click="addPasskey"
                    />
                </div>

                <div v-if="!passkeys.length" class="text-[11px] text-slate-400 mt-2">{{ $t('passkeys.empty') }}</div>

                <ul v-else class="flex flex-col gap-2 mt-3">
                    <li
                        v-for="passkey in passkeys"
                        :key="passkey.id"
                        class="flex items-center justify-between gap-3 rounded-md bg-slate-50 dark:bg-slate-900/60 px-3 py-2"
                    >
                        <div class="min-w-0">
                            <div class="text-sm font-medium truncate dark:text-slate-100">
                                <i class="pi pi-key text-[11px] mr-1 text-slate-400" />{{ passkey.label }}
                            </div>
                            <div class="text-[11px] text-slate-400">
                                {{ $t('passkeys.created') }}: {{ formatDateTime(passkey.created_at) }} ·
                                {{ $t('passkeys.last_used') }}: {{ formatUsed(passkey.last_used_at) }}
                            </div>
                        </div>
                        <Button
                            icon="pi pi-trash"
                            severity="danger"
                            size="small"
                            text
                            type="button"
                            @click="removePasskey(passkey)"
                        />
                    </li>
                </ul>
            </div>

            <div class="flex justify-end gap-2 mt-2">
                <Button :label="$t('general.cancel')" severity="secondary" type="reset" @click="visible = false" />
                <Button :label="$t('general.save')" :loading="appStore.isLoaderActive()" type="submit" />
            </div>
        </AppForm>

        <!-- Name prompt for a new passkey (e.g. "MacBook Touch ID"). -->
        <Dialog v-model:visible="nameDialogVisible" :header="$t('passkeys.add')" class="w-96" modal>
            <AppInputText v-model="newLabel" :label="$t('passkeys.label')" name="passkey_label" />
            <div class="flex justify-end gap-2 mt-5">
                <Button :label="$t('general.cancel')" severity="secondary" @click="nameDialogVisible = false" />
                <Button :label="$t('general.confirm')" @click="confirmAddPasskey" />
            </div>
        </Dialog>

        <AppConfirmDialog ref="confirmDialog" />
    </Dialog>
</template>

<script lang="ts" setup>
    import { Button, Dialog } from 'primevue'
    import { computed, ref, useTemplateRef } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AppConfirmDialog from '@/components/app/AppConfirmDialog.vue'
    import AppDropdown from '@/components/app/AppDropdown.vue'
    import AppForm from '@/components/app/AppForm.vue'
    import AppInputText from '@/components/app/AppInputText.vue'
    import { formatDateTime } from '@/libs/dayjs'
    import { parseError } from '@/libs/error'
    import { PrintError, PrintSuccess } from '@/libs/toast'
    import { attestationToJSON, isPasskeySupported, prepareCreation } from '@/libs/webauthn'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'

    interface Passkey {
        id:            string
        label:         string
        created_at:    string
        last_used_at?: string
    }

    const { t }      = useI18n()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const visible = ref(false)

    const confirmDialog = useTemplateRef<InstanceType<typeof AppConfirmDialog>>('confirmDialog')

    const passkeyEnabled     = ref(false)
    const passkeys           = ref<Passkey[]>([])
    const passkeyBusy        = ref(false)
    const nameDialogVisible  = ref(false)
    const newLabel           = ref('')

    const passkeyAvailable = computed(() => passkeyEnabled.value && isPasskeySupported())

    // The API stores a zero time for a passkey that was never used to sign in;
    // render that as an em dash rather than "01.01.0001".
    const formatUsed = (value?: string): string => {
        if (!value || value.startsWith('0001-01-01')) return '—'
        return formatDateTime(value)
    }

    const loadPasskeys = async () => {
        const { statusCode, data } = await fetchStore.fetch('/passkey').get().json<Passkey[]>()
        passkeys.value = statusCode.value === 200 ? (data.value ?? []) : []
    }

    const addPasskey = () => {
        newLabel.value = ''
        nameDialogVisible.value = true
    }

    const confirmAddPasskey = async () => {
        nameDialogVisible.value = false
        passkeyBusy.value = true

        try {
            const begin = await fetchStore.fetch('/passkey/register/begin').post({}).json<{ options: { publicKey: unknown }, handle: string }>()
            if (begin.statusCode.value !== 200 || !begin.data.value) {
                PrintError(t('passkeys.failed'))
                return
            }

            const publicKey  = prepareCreation(begin.data.value.options.publicKey)
            const credential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential

            const finish = await fetchStore
                .fetch('/passkey/register/finish')
                .post({ handle: begin.data.value.handle, label: newLabel.value, credential: attestationToJSON(credential) })
                .json()

            if (finish.response.value?.status === 200) {
                PrintSuccess(t('passkeys.added'))
                await loadPasskeys()
            } else {
                PrintError(t('passkeys.failed'))
            }
        } catch {
            // Includes the user dismissing the OS/browser prompt.
            PrintError(t('passkeys.failed'))
        } finally {
            passkeyBusy.value = false
        }
    }

    const removePasskey = async (passkey: Passkey) => {
        if (!await confirmDialog.value?.confirm(t('passkeys.confirm_delete', { label: passkey.label }))) return

        const { response } = await fetchStore.fetch(`/passkey/${passkey.id}`).delete().json()

        if (response.value?.status === 200) {
            PrintSuccess(t('success.deleted'))
            await loadPasskeys()
        } else {
            PrintError(t('errors.generic_error'))
        }
    }

    const languageOptions = [
        { value: 'sl', label: 'Slovenščina' },
        { value: 'en', label: 'English' },
    ]

    const getEmptyForm = () => ({
        name: '', email: '', language: 'sl', current_password: '', new_password: '',
    })

    const form            = ref(getEmptyForm())
    const confirmPassword = ref('')

    const show = async () => {
        form.value = {
            ...getEmptyForm(),
            name:     appStore.userData?.name ?? '',
            email:    appStore.userData?.email ?? '',
            language: appStore.userData?.language ?? appStore.locale ?? 'sl',
        }
        confirmPassword.value = ''
        visible.value = true

        const { data } = await fetchStore.fetch('/passkey/enabled').get().json<{ enabled: boolean }>()
        passkeyEnabled.value = !!data.value?.enabled

        if (passkeyEnabled.value) await loadPasskeys()
    }

    const reset = () => { form.value = getEmptyForm(); confirmPassword.value = '' }

    const save = async () => {
        // Password change: new + confirm must match when a new password is entered.
        if (form.value.new_password && form.value.new_password !== confirmPassword.value) {
            PrintError(t('profile.password_mismatch'))
            return
        }

        appStore.startLoading()

        const { response, data } = await fetchStore.fetch('/me/profile').put(form.value).json()

        if (response.value?.status === 200) {
            PrintSuccess(t('success.edit'))
            // Reflect the changes locally so the topbar + language update at once.
            if (appStore.userData) {
                appStore.userData.name  = form.value.name
                appStore.userData.email = form.value.email
                appStore.userData.language = form.value.language
            }
            if (form.value.language) appStore.changeLocale(form.value.language)
            visible.value = false
        } else if (response.value?.status === 401) {
            PrintError(t('profile.wrong_password'))
        } else {
            PrintError(parseError(data.value, t('errors.on_edit')))
        }

        appStore.stopLoading()
    }

    defineExpose({ show })
</script>
